import type { ValidationReportItem } from '@/domains/report/api/types';

import { matchesFilter } from './filterCriteria';

/** Key and label of the bucket for criteria without a group in the active combination. */
export const UNGROUPED_KEY = 'ungrouped';
export const UNGROUPED_LABEL = 'Overig';

export type CriterionGroupBucket = {
  /** Group id, or `UNGROUPED_KEY`. Used as accordion item value. */
  key: string;
  name: string;
  items: ValidationReportItem[];
};

export type CriterionGroupCounts = {
  total: number;
  present: number;
  inconsistent: number;
  missing: number;
};

/**
 * Buckets items per group. A criterion linked to several groups appears in
 * each of them; criteria without a group go to "Overig", which is always last.
 * Groups sort by name (nl). Items keep the API order.
 */
export function bucketByGroup(items: ValidationReportItem[]): CriterionGroupBucket[] {
  const buckets = new Map<string, CriterionGroupBucket>();
  for (const item of items) {
    const targets =
      item.groups.length === 0
        ? [{ id: UNGROUPED_KEY, name: UNGROUPED_LABEL }]
        : item.groups;
    for (const group of targets) {
      const bucket = buckets.get(group.id) ?? {
        key: group.id,
        name: group.name,
        items: [],
      };
      bucket.items.push(item);
      buckets.set(group.id, bucket);
    }
  }
  return [...buckets.values()].sort((a, b) => {
    if (a.key === UNGROUPED_KEY) return 1;
    if (b.key === UNGROUPED_KEY) return -1;
    return a.name.localeCompare(b.name, 'nl');
  });
}

/** Counts for a group header. Uses the same rules as the filter buttons. */
export function countGroup(items: ValidationReportItem[]): CriterionGroupCounts {
  return {
    total: items.length,
    present: items.filter((item) => matchesFilter(item, 'present')).length,
    inconsistent: items.filter((item) => matchesFilter(item, 'inconsistent')).length,
    missing: items.filter((item) => matchesFilter(item, 'missing')).length,
  };
}

/** False when no group is configured for the active combination. */
export function hasConfiguredGroups(items: ValidationReportItem[]): boolean {
  return items.some((item) => item.groups.length > 0);
}
